import React from "react";

interface SidebarProps {
  currentView: string;
  onNavigate: (view: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  user: any;
  onLogout: () => void;
  allowed?: string[];
}

interface NavItem {
  id: string;
  label: string;
  icon: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const GROUPS: NavGroup[] = [
  { label: "Overview", items: [
    { id: "dashboard", label: "Dashboard", icon: "🏠" },
    { id: "cqc-hq", label: "CQC HQ", icon: "🏆" },
    { id: "audit-engine", label: "Audit Engine", icon: "📋" },
    { id: "action-plans", label: "Action Plans", icon: "🎯" },
    { id: "intelligence", label: "AI Intelligence", icon: "🤖" },
    
  ]},
  { label: "Care & Clinical", items: [
    { id: "admissions", label: "Admissions", icon: "🛏️" },
    { id: "resident-360", label: "Resident 360", icon: "🧑‍⚕️" },
    { id: "admissions-bundle", label: "Admissions Bundle", icon: "🧳" },
    { id: "enhanced-admissions", label: "Pre-Admission Assessment", icon: "🚪" },
    { id: "dependency-pricing", label: "Dependency Pricing", icon: "🧮" },
    { id: "dependency-tool", label: "Dependency Assessment", icon: "📋" },
    { id: "medication-suite", label: "Medication Safety", icon: "💊" },
    { id: "med-competency", label: "Med Competency & CD", icon: "🎓" },
    { id: "ipc", label: "IPC & Outbreak", icon: "🦠" },
    { id: "residents", label: "Residents & Invoices", icon: "💷" },
    { id: "wound-care", label: "Wound Care", icon: "🩹" },
    { id: "safeguarding", label: "Safeguarding", icon: "🛡️" },
    { id: "medication-checks", label: "Medication Checks", icon: "💊" },
    { id: "dols", label: "MCA / DoLS", icon: "⚖️" },
    { id: "mca-hub", label: "MCA Excellence Hub", icon: "🧠" },
    { id: "call-log", label: "Call Log", icon: "📞" },
    { id: "shift-handover", label: "Shift Handover", icon: "🔄" },
    { id: "walkarounds", label: "Walkarounds", icon: "🚶" },
    { id: "reconciliation", label: "Reconciliation", icon: "💳" },
    { id: "bulk-upload", label: "Bulk Upload & Auto Billing", icon: "📥" },
    { id: "wellbeing", label: "Wellbeing Tracker", icon: "🌟" },
    { id: "activities", label: "Activities & Community", icon: "🎨" },
    { id: "end-of-life", label: "End-of-Life Care", icon: "🕊️" },
  ]},
  { label: "Daily Operations", items: [
    { id: "dining", label: "Dining & Meals", icon: "🍽️" },
    { id: "cleaning", label: "Cleaning", icon: "🧽" },
    { id: "stock", label: "Stock", icon: "📦" },
    { id: "file-vault", label: "File Vault", icon: "🗂️" },
  ]},
  { label: "People & Policies", items: [
    { id: "policies", label: "Policies & SOPs", icon: "📚" },
    { id: "staff", label: "Staff", icon: "👥" },
    { id: "training", label: "Training", icon: "🎓" },
    { id: "feedback", label: "Feedback", icon: "💬" },
  ]},
  { label: "Governance", items: [
    { id: "audits", label: "Audits", icon: "🔍" },
    { id: "compliance", label: "Compliance", icon: "✅" },
    { id: "cqc-readiness", label: "CQC Readiness", icon: "📈" },
    { id: "evidence-pack", label: "Evidence Pack", icon: "📁" },
    { id: "you-said-we-did", label: "You Said, We Did", icon: "🗣️" },
    { id: "near-miss", label: "Near-Miss Learning", icon: "⚡" },
    { id: "trends", label: "Trend Dashboard", icon: "📈" },
    { id: "staff-wellbeing", label: "Staff Wellbeing", icon: "💚" },
    { id: "partnerships", label: "Partnerships & Innovation", icon: "🤝" },
    { id: "scrutiny", label: "LA & External Scrutiny", icon: "🏛️" },
    { id: "hardening", label: "Zero-Hole Hardening", icon: "🛡️" },
    { id: "deep-audit", label: "Deep Audit Engine", icon: "🔍" },
    { id: "cqc-action-plan", label: "CQC Action Plan", icon: "🎯" },
    { id: "reports", label: "Reports", icon: "📊" },
    { id: "deadlines", label: "Statutory Deadlines", icon: "⏰" },
    { id: "lifecycle-test", label: "Lifecycle Test Suite", icon: "🧪" },
    { id: "user-guide", label: "Training Manual & Guide", icon: "📖" },
       { id: "pcs-sync", label: "PCS Integration", icon: "🔗" },
    { id: "settings", label: "Settings", icon: "⚙️" },
  ]},
];

export function Sidebar({ currentView, onNavigate, isOpen, onToggle, user, onLogout, allowed }: SidebarProps) {
  const groups = GROUPS
    .map(group => ({ ...group, items: group.items.filter(item => !allowed || allowed.includes(item.id)) }))
    .filter(group => group.items.length > 0);

  return (
    <aside className={`${isOpen ? "w-64" : "w-16"} flex h-screen flex-col bg-[#123B36] text-white transition-all`}>
      <div className="flex items-center gap-2 border-b border-white/10 px-3 py-3">
        <button onClick={onToggle} className="rounded-lg px-2 py-1 text-lg hover:bg-white/10">☰</button>
        <img src="/logo512.png" alt="Roop Cottage logo" className="h-9 w-9 rounded-lg object-contain" />
        {isOpen ? <span className="text-sm font-bold tracking-wide">ROOP COTTAGE</span> : null}
      </div>

      <nav className="flex-1 space-y-4 overflow-y-auto px-2 py-4">
        {groups.map(group => (
          <div key={group.label}>
            {isOpen ? <div className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-200/70">{group.label}</div> : null}
            <div className="space-y-1">
              {group.items.map(item => (
                <button
                  key={item.id}
                  onClick={() => onNavigate(item.id)}
                  title={item.label}
                  className={`${currentView === item.id ? "bg-white/15 font-medium" : "hover:bg-white/5"} flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm`}
                >
                  <span>{item.icon}</span>
                  {isOpen ? <span>{item.label}</span> : null}
                </button>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-white/10 p-3">
        {isOpen ? <div className="mb-2 truncate text-xs text-emerald-200/70">{user && user.name ? user.name : ""}</div> : null}
        <button onClick={onLogout} className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/20">
          {isOpen ? "Sign out" : "⎋"}
        </button>
      </div>
    </aside>
  );
}

export function TopBar({ user, onToggleSidebar }: { user: any; onToggleSidebar: () => void }) {
  const today = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3">
      <div>
        <div className="text-sm font-semibold text-slate-900">Roop Cottage Residential Care Home</div>
        <div className="text-xs text-slate-500">Wakefield Road, Fitzwilliam, Pontefract, WF9 5AN · {today}</div>
      </div>
      <div className="flex items-center gap-3">
        <span className="rounded-full bg-[#123B36] px-3 py-1 text-xs font-medium text-white">{user && user.role ? user.role : "User"}</span>
        <span className="text-sm text-slate-700">{user && user.name ? user.name : ""}</span>
      </div>
    </header>
  );
}
